import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, X } from "@phosphor-icons/react";

const LEAVE_TYPES = [
  { value: "ANNUAL", key: "annual" },
  { value: "SICK", key: "sick" },
  { value: "UNPAID", key: "unpaid" },
  { value: "WFH", key: "wfh_short" },
  { value: "EXTRAORDINARY", key: "extraordinary" },
  { value: "COMPENSATORY", key: "compensatory" },
  { value: "BLOOD_DONATION", key: "blood_donation" },
  { value: "ESCORT", key: "escort" },
  { value: "OVERTIME", key: "overtime" },
  { value: "TIME_CORRECTION", key: "time_correction" },
];

const STATUS_COLORS = {
  PENDING: "bg-[#FFCC00] text-black",
  APPROVED: "bg-[#00CC33] text-black",
  REJECTED: "bg-[#FF3333] text-white",
  CANCELLED: "bg-black text-white",
};

function today() { return new Date().toISOString().slice(0,10); }

export default function Leave() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "ANNUAL", start_date: today(), end_date: today(), days: 1, reason: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/leave/requests", { params: { mine: true } });
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leave/requests", {
        type: form.type,
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
        days: Number(form.days),
        reason: form.reason,
      });
      toast.success("Request submitted");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/leave/requests/${id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/60">{t("leave")}</div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-none">My leave requests</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="brut-border bg-white p-3 flex gap-4" data-testid="vacation-balance">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-black/60">{t("vacation_current_year")}</div>
              <div className="font-display font-black text-2xl tracking-tighter leading-none">{user.vacation_balance_days}</div>
            </div>
            <div className="border-l-2 border-black/10 pl-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-black/60">{t("vacation_prior_year")}</div>
              <div className="font-display font-black text-2xl tracking-tighter leading-none">{user.vacation_balance_prior_year ?? 0}</div>
            </div>
            <div className="border-l-2 border-black/10 pl-4 bg-[#FFCC00] -m-3 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider">{t("total_balance")}</div>
              <div className="font-display font-black text-2xl tracking-tighter leading-none">
                {(user.vacation_balance_days || 0) + (user.vacation_balance_prior_year || 0)}
              </div>
            </div>
          </div>
          <button
            data-testid="new-leave-btn"
            onClick={() => setOpen(true)}
            className="bg-black text-white brut-border brut-shadow brut-hover px-4 py-3 font-bold uppercase tracking-wider text-xs flex items-center gap-2"
          >
            <Plus size={16} weight="bold" /> {t("request_leave")}
          </button>
        </div>
      </div>

      <div className="brut-border bg-white brut-shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F4F0] uppercase tracking-wider text-xs">
              <tr>
                <th className="text-left p-3 border-b-2 border-black">{t("type")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("start_date")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("end_date")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("days")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("status")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("reason")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("action")}</th>
              </tr>
            </thead>
            <tbody data-testid="leave-table">
              {items.map(i => (
                <tr key={i.id} className="border-b border-black/10">
                  <td className="p-3 font-bold">{i.type}</td>
                  <td className="p-3 font-mono text-xs">{new Date(i.start_date).toLocaleDateString()}</td>
                  <td className="p-3 font-mono text-xs">{new Date(i.end_date).toLocaleDateString()}</td>
                  <td className="p-3 font-bold">{i.days}</td>
                  <td className="p-3"><span className={`tag ${STATUS_COLORS[i.status]}`}>{t(i.status.toLowerCase())}</span></td>
                  <td className="p-3 text-xs max-w-xs truncate">{i.reason || "—"}</td>
                  <td className="p-3">
                    {i.status === "PENDING" && (
                      <button
                        data-testid={`cancel-leave-${i.id}`}
                        onClick={() => cancel(i.id)}
                        className="brut-border px-2 py-1 text-xs font-bold uppercase bg-white hover:bg-[#FF3333] hover:text-white"
                      >{t("cancel")}</button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-black/60">{t("no_data")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setOpen(false)}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            data-testid="leave-dialog"
            className="brut-border bg-white p-6 brut-shadow-lg max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-black text-2xl tracking-tighter">{t("request_leave")}</div>
              <button type="button" onClick={() => setOpen(false)} data-testid="close-dialog"><X size={20} weight="bold" /></button>
            </div>

            <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("leave_type")}</label>
            <select
              data-testid="leave-type"
              value={form.type}
              onChange={(e) => setForm({...form, type: e.target.value})}
              className="w-full brut-border p-2 mb-3 bg-white"
            >
              {LEAVE_TYPES.map(x => <option key={x.value} value={x.value}>{t(x.key)}</option>)}
            </select>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("start_date")}</label>
                <input type="date" data-testid="leave-start" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} className="w-full brut-border p-2" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("end_date")}</label>
                <input type="date" data-testid="leave-end" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} className="w-full brut-border p-2" />
              </div>
            </div>

            <label className="block text-xs font-bold uppercase tracking-wider mt-3 mb-1">{t("days")}</label>
            <input type="number" step="0.5" min="0" data-testid="leave-days" value={form.days} onChange={(e) => setForm({...form, days: e.target.value})} className="w-full brut-border p-2" />

            <label className="block text-xs font-bold uppercase tracking-wider mt-3 mb-1">{t("reason")}</label>
            <textarea data-testid="leave-reason" value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} className="w-full brut-border p-2 min-h-[80px]" />

            <button
              disabled={loading}
              type="submit"
              data-testid="leave-submit"
              className="mt-4 w-full bg-black text-white brut-border brut-shadow brut-hover p-3 font-bold uppercase tracking-wider disabled:opacity-60"
            >
              {loading ? "…" : t("submit")}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
